/**
 * The line-item screen's decisions, where a runner can reach them — the reason
 * `campaigns.ts` gives for being a module rather than a component.
 *
 * 🔴 **The lock is not re-implemented here.** `createOutcome`, `createThrew`,
 * `mayCreateAgain`, `foundById` and `outcomeAfterReload` in `campaigns.ts` already
 * carry the reasoning that cost twads#86 and twads#87 to get right, and that
 * reasoning is about an answer's shape — `created: true`, `created: "unknown"`, an
 * id or none — not about campaigns. A second copy of it would be a second place to
 * fix, and the copy is the one that would not be fixed.
 *
 * What genuinely differs is one thing: the payload a 201 carries. A campaign's
 * answer brings `servable` and `reasonsNotServable`; a line item's brings neither,
 * because X's line item object has neither. So only that mapping is written here,
 * and the three locking outcomes are passed through exactly as they arrive.
 */
import type { LineItem } from "./api.ts";
import { type CreateOutcome, createOutcome } from "./campaigns.ts";

/** What a created line item is, as this screen knows it. */
export interface LineItemState {
	id: string;
	entityStatus: string;
	/** X's row, when the answer carried it. The screen shows the flight from here. */
	lineItem: LineItem | null;
}

/**
 * How a line-item create ended. The three locking kinds are `campaigns.ts`'s own,
 * word for word, so a screen can treat them identically — and must.
 */
export type LineItemOutcome =
	| ({ kind: "created" } & LineItemState)
	| Exclude<CreateOutcome, { kind: "created" }>;

const record = (body: unknown): Record<string, unknown> =>
	typeof body === "object" && body !== null && !Array.isArray(body)
		? (body as Record<string, unknown>)
		: {};

/**
 * The create's answer, read through the campaign lock and then re-read for the one
 * field that differs.
 *
 * 🔴 The order matters and is the point: `createOutcome` decides FIRST, so
 * `created: true` and `created: "unknown"` win over the status, over the body's
 * shape, and over anything this function would otherwise conclude. Only a 201 that
 * the lock itself called `created` reaches the mapping below.
 */
export function lineItemCreateOutcome(status: number, body: unknown): LineItemOutcome {
	const base = createOutcome(status, body);
	if (base.kind !== "created") return base;
	const b = record(body);
	const row = record(b.lineItem);
	return {
		kind: "created",
		id: base.id,
		entityStatus: base.entityStatus,
		// Only a row that names the same line item the answer did. A body whose
		// `lineItem` is null — or is some other line item — leaves this null rather than
		// putting a flight on screen that belongs to something else.
		lineItem: typeof row.id === "string" && row.id === base.id ? (b.lineItem as LineItem) : null,
	};
}

/**
 * Whether a line item this screen has just created is still unnarrowed.
 *
 * 🔴 **The mechanism in this comment had expired and is corrected, 2026-10-08.** It
 * read: *"delivery needs at least one targeting criterion, and v1 has no endpoint for
 * one"* — written before `POST /api/v1/targeting-criteria` arrived. v1 has that
 * endpoint, and the Targeting button on each row opens it. What is still true is the
 * fact the banner needs: the create sends no criteria, so a line item that has only
 * just been created has none until somebody adds one.
 *
 * It is a function rather than a constant because the honest answer depends on
 * something this console cannot see — X's own targeting criteria, which may have
 * been added elsewhere. So it says what THIS console did, and not what X holds.
 *
 * 🔵 And it was **unused** until 2026-10-08, with its condition written out again
 * inside `LineItems.tsx`. Two copies of one decision, and the dead one is the one
 * carrying the reasoning — so the component now calls it.
 */
export function cannotDeliverYet(outcome: LineItemOutcome | null): boolean {
	return outcome?.kind === "created" || outcome?.kind === "exists";
}

/** Which way the switch was asked. The worker accepts these two and nothing else. */
export type AskedStatus = "ACTIVE" | "PAUSED";

/**
 * How one switch of a line item ended, as this screen knows it.
 *
 * 🔴 **Four kinds and not three, because "nothing was written" is a different fact
 * from "the write happened and X disagrees".** A 400 or a 403 leaves the line item
 * exactly as it was; a 502 means X took the write and then reported another status,
 * so the object may have moved. An operator deciding whether to look again needs
 * those apart, and a single `error` kind would have hidden it.
 *
 * `disagreed` and `refused` carry the worker's own sentence and never a sentence
 * built here: the worker's 502 is the only place that knows the direction's
 * consequence (a stop that did not take leaves money moving; a start that did not
 * take leaves nothing delivering), and re-deriving it on this side would be a second
 * place to get it wrong.
 */
export type StatusOutcome =
	| { kind: "reported"; asked: AskedStatus; status: number; reported: string }
	| { kind: "disagreed"; asked: AskedStatus; status: number; error: string | null }
	| { kind: "refused"; asked: AskedStatus; status: number; error: string | null }
	| { kind: "unreadable"; asked: AskedStatus; status: number };

/**
 * Read the switch's answer.
 *
 * 🔴 **The kind comes from the HTTP status and never from the body's shape; the body
 * only fills in the words.** That order is the whole design: a 502 whose body could
 * not be read is still `disagreed`, because "X took the write and reports another
 * status" is known from the status alone, and demoting it to `unreadable` would drop
 * the one fact that decides whether an operator must look again. So `error` is
 * nullable rather than the kind being conditional on it.
 *
 * 🔴 **And nothing here ever interpolates a missing field into a sentence.** The
 * screen used to build its own text from the body, so a 200 carrying no
 * `entityStatus` printed *"X reports undefined."* — which reads as an answer from X
 * and is a missing field on this side. A 200 that cannot be read is `unreadable` and
 * carries the number instead.
 */
export function lineItemStatusOutcome(
	asked: AskedStatus,
	status: number,
	body: unknown,
): StatusOutcome {
	const b = record(body);
	const error = typeof b.error === "string" && b.error !== "" ? b.error : null;
	if (status === 502) return { kind: "disagreed", asked, status, error };
	if (status !== 200) return { kind: "refused", asked, status, error };
	return typeof b.entityStatus === "string" && b.entityStatus !== ""
		? { kind: "reported", asked, status, reported: b.entityStatus }
		: { kind: "unreadable", asked, status };
}

/**
 * Whether a start needs confirming first, and why.
 *
 * 🔴 **The console asks rather than refuses, and that division is deliberate** (the
 * owner, 2026-10-08). Starting delivery is the owner's act; what this console owes
 * them is the price of the act, named at the moment of the act. A refusal here would
 * also be the wrong place for it: the worker's one condition on `ACTIVE` is the
 * parent campaign, by the same decision, and a route that also judged targeting
 * would be guessing at X's rules — X serves an untargeted line item perfectly
 * happily. So this is the SCREEN's gate, and the route stays as documented.
 *
 * 🔴 **`unknown` is not folded into `untargeted`.** A criteria read that failed says
 * nothing about the targeting, and the screen's own rule is never to turn a network
 * error into "reaches everyone". Both still confirm — one click against reaching the
 * world is cheap — but they confirm with different sentences, because telling someone
 * "this has no targeting" when nobody knows is the same invention in a smaller font.
 */
export type StartGate = "ready" | "untargeted" | "excludes-only" | "unknown";

/**
 * 🔴 **Since targeting v2 (2026-10-29) a criterion can exclude.** Until then any criterion
 * narrowed, so «not empty» meant «narrowed». An NE takes one place out and adds none, so a
 * line item whose every criterion is NE is not narrowed to anywhere it targets: it is its own
 * gate, `excludes-only`, warned about as a start is warned about an empty list. Only an
 * explicit `NE` counts as excluding — a row whose operator is not named is read as it was
 * before v2, as narrowing, because nothing here guesses an operator (as the worker does not).
 */
export function startGate(
	criteria: readonly { id?: string; operatorType?: string | null }[] | null,
): StartGate {
	if (criteria === null) return "unknown";
	if (criteria.length === 0) return "untargeted";
	return criteria.every((c) => c.operatorType === "NE") ? "excludes-only" : "ready";
}

/**
 * A start that has been warned about and not yet confirmed, with the line item it
 * belongs to.
 *
 * 🔴 **The id is the whole point of this type.** The confirmation used to be held as a
 * bare string, and `startWarning` returns a CONSTANT sentence for a given gate — so two
 * untargeted line items produce the same text, and "has this operator already seen this
 * warning?" answered yes for a line item they had never pressed Start on. The panel is
 * one component re-pointed at another row rather than a new one, so the state outlived
 * the object it was about: one press on the second line item sent `ACTIVE` with no
 * warning at all (d6, 2026-10-08). A comparison of sentences cannot tell two objects
 * apart; a comparison that carries the id can.
 */
export interface PendingStart {
	lineItemId: string;
	text: string;
}

/**
 * Whether this operator has already been shown THIS warning for THIS line item — the
 * one question that decides whether a press sends or asks.
 *
 * It is a function in this module rather than a comparison in the component because
 * the defect it exists for was invisible to a test that read the component's text: the
 * staleness lived in what the screen did NOT reset, which no reading of `switchTo`
 * could see. Here it is decidable without rendering anything, so the case for it
 * measures the state after the line item changes instead of the shape of a function.
 */
export function startAlreadyAsked(
	lineItemId: string,
	warning: string,
	pending: PendingStart | null,
): boolean {
	return pending !== null && pending.lineItemId === lineItemId && pending.text === warning;
}
